import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { FieldArray, getIn, useFormikContext } from "formik";
import React from "react";
import { Plus, Trash } from "react-feather";
import { EditMode, EmployeeAccount } from "../../../models";
import { PlanFormValues } from "../PlanFormValues";

type AllowancesTiersFieldsProps = {
  isPilot: boolean;
  accounts: EmployeeAccount[];
  editMode: EditMode;
};

export const AllowancesTiersFields: React.FC<AllowancesTiersFieldsProps> = ({
  isPilot,
  accounts,
  editMode
}) => {
  const { values, errors, touched, handleChange, setFieldValue } =
    useFormikContext<PlanFormValues>();

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        padding={0}
      >
        <Typography
          variant="subtitle1"
          sx={{ display: "flex", alignItems: "center" }}
        >
          Contribution tiers
        </Typography>
        <IconButton
          disabled={isPilot}
          onClick={() => {
            setFieldValue("tiers", [
              ...values.tiers,
              {
                id: undefined,
                name: "",
                amount: { amount: 0 }
              }
            ]);
          }}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Plus />
        </IconButton>
      </Box>

      <FieldArray name="tiers">
        {() => (
          <List>
            {values.tiers.map((tier, index) => {
              const canDelete =
                tier.id === undefined ||
                !accounts.some((a) => a.tierId === tier.id);
              const tooltipTitle = !canDelete
                ? "Unable to remove the tier as it currently has employees assigned to it."
                : "Remove tier";

              const amountError =
                getIn(touched, `tiers[${index}].amount.amount`) &&
                getIn(errors, `tiers[${index}].amount.amount`);

              const tierNameError =
                getIn(touched, `tiers[${index}].name`) &&
                getIn(errors, `tiers[${index}].name`);

              return (
                <ListItem
                  key={index}
                  sx={{ paddingLeft: 0 }}
                  secondaryAction={
                    <Tooltip title={tooltipTitle}>
                      <div>
                        <IconButton
                          disabled={!canDelete || isPilot}
                          edge="end"
                          aria-label="delete"
                          onClick={() => {
                            const newTiers = [...values.tiers];
                            newTiers.splice(index, 1);
                            setFieldValue("tiers", newTiers);
                          }}
                        >
                          <Trash />
                        </IconButton>
                      </div>
                    </Tooltip>
                  }
                >
                  <Grid container alignItems="center">
                    <Grid item xs={9}>
                      <TextField
                        disabled={isPilot}
                        variant="standard"
                        fullWidth
                        value={tier.name}
                        name={`tiers[${index}].name`}
                        onChange={handleChange}
                        error={Boolean(tierNameError)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        disabled={isPilot}
                        type="number"
                        variant="standard"
                        fullWidth
                        name={`tiers[${index}].amount.amount`}
                        value={tier.amount.amount}
                        onChange={handleChange}
                        error={Boolean(amountError)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    {tierNameError && (
                      <Typography color="error" sx={{ mt: 2 }}>
                        {tierNameError}
                      </Typography>
                    )}
                    {amountError && (
                      <Typography color="error" sx={{ mt: 2 }}>
                        {amountError}
                      </Typography>
                    )}
                  </Grid>
                </ListItem>
              );
            })}
          </List>
        )}
      </FieldArray>

      {touched.tiers && errors.tiers && typeof errors.tiers === "string" && (
        <Typography color="error" sx={{ mt: 2 }}>
          {errors.tiers}
        </Typography>
      )}

      {editMode === EditMode.EDIT && (
        <Typography sx={{ mt: 2, mb: 4 }} variant="body2">
          Changes to tiers will be effective on the next renewal date
        </Typography>
      )}
    </Box>
  );
};
